import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { shows as fallbackShows, type Show } from '../data';
import { db } from './firebase';

export type ShowRecord = Show & {
  id: string;
  startsOn: string;
  city: string;
};

export type ShowInput = {
  startsOn: string;
  city: string;
  venue: string;
  location: string;
  note: string;
};

const showsRef = collection(db, 'shows');

export function formatShowDate(startsOn: string, city: string) {
  if (!startsOn) return city;
  const [year, month, day] = startsOn.split('-').map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  const chip = date
    .toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
    .replace(',', '')
    .toUpperCase();
  return city ? `${chip} · ${city}` : chip;
}

export function toPublicShow(record: ShowRecord): Show {
  return {
    date: formatShowDate(record.startsOn, record.city),
    venue: record.venue,
    location: record.location,
    note: record.note || undefined,
  };
}

function fromDoc(id: string, data: Record<string, unknown>): ShowRecord {
  return {
    id,
    startsOn: typeof data.startsOn === 'string' ? data.startsOn : '',
    city: typeof data.city === 'string' ? data.city : '',
    venue: typeof data.venue === 'string' ? data.venue : '',
    location: typeof data.location === 'string' ? data.location : '',
    note: typeof data.note === 'string' ? data.note : '',
    date: formatShowDate(
      typeof data.startsOn === 'string' ? data.startsOn : '',
      typeof data.city === 'string' ? data.city : '',
    ),
  };
}

export function useShows() {
  const [shows, setShows] = useState<ShowRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(showsRef, orderBy('startsOn', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setShows(snap.docs.map((item) => fromDoc(item.id, item.data())));
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { shows, loading, error };
}

export function usePublicShows() {
  const live = useShows();
  if (live.loading) {
    return { shows: [] as Show[], loading: true, error: live.error };
  }
  if (live.error) {
    return { shows: fallbackShows, loading: false, error: live.error };
  }
  return { shows: live.shows.map(toPublicShow), loading: false, error: null };
}

export async function createShow(input: ShowInput) {
  await addDoc(showsRef, {
    startsOn: input.startsOn,
    city: input.city.trim(),
    venue: input.venue.trim(),
    location: input.location.trim(),
    note: input.note.trim(),
  });
}

export async function updateShow(id: string, input: ShowInput) {
  await updateDoc(doc(db, 'shows', id), {
    startsOn: input.startsOn,
    city: input.city.trim(),
    venue: input.venue.trim(),
    location: input.location.trim(),
    note: input.note.trim(),
  });
}

export async function deleteShow(id: string) {
  await deleteDoc(doc(db, 'shows', id));
}

export const defaultShowSeed: ShowInput[] = [
  { startsOn: '2026-01-18', city: 'Roseville, CA', venue: 'Goldfield Trading Post', location: 'Co-headline w/ special guest', note: '' },
  { startsOn: '2026-02-08', city: 'Sacramento, CA', venue: 'The Guild Theater', location: 'Nashville Nights Series', note: '' },
  { startsOn: '2026-03-14', city: 'Chico, CA', venue: 'The Sierra Nevada Big Room', location: '90s Country Throwdown', note: '' },
  { startsOn: '2026-04-12', city: 'Yuba City, CA', venue: 'Sutter Theater Center for the Arts', location: 'Concert for a Cause', note: '' },
  { startsOn: '2026-05-24', city: 'Napa, CA', venue: 'BottleRock Late Night', location: 'Official Afterparty', note: '' },
];
